import React, { useState, useEffect } from "react";
import s from "./ControlCierreTJ.module.css";
import { useNavigate } from "react-router-dom";
import {
  setConcentrator,
  setSelectedAgencieToConsult,
} from "../../../store/slices/my_agencies";
import { today } from "../../../helpers/today";
import {
  Grid,
  TableContainer,
  Table,
  TableHead,
  Button,
  TableRow,
  TableCell,
  TableBody,
  Box,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { getCierreTJ } from "../../../api/saaservices/cashFlow";
import { exportExcel } from "../../../helpers/exportExcel";
import { Currency } from "../../../helpers/currencyConvert";
import { useLocation } from "react-router-dom";
import AgencySelectModal from "../../Shared/AgencySelectModal/AgencySelectModal";
import CircularProgress from "@mui/material/CircularProgress";
import {
  AgencyPage,
  getPresentAgencyInPath,
  logOutPath,
  hasAgencyInPath,
  getAgencyNumberDisplay,
} from "../../../helpers/path";
import printTable from "../../../helpers/imprimir";
import { agencieSearchByNumber } from "../../../api/agencies/AgenciesService";
import { updateLocation } from "../../../store/slices/localSlice";
import { getSaaId } from "../../../utils/session.utils";
import { getPrintLogo } from "../../../api/themes/ThemeService";
import { CalendarioHoy } from "../../../helpers/today";
import ModalFiltros from "./ModalFiltros";
import {
  filterByCierre,
  getCierreLabelKey,
  sortCierres,
} from "./cierreTipo";

export const ControlCierreTJ = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const agencies = useSelector((state) => state.myAgencies.user_agencies);
  const { activeOrganization } = useSelector((state) => state.gamma);
  const navigate = useNavigate();
  const agencieSelected = useSelector(
    (state) => state.myAgencies?.selected_agencie_to_consult,
  );
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [search, setSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [filters, setFilters] = useState({ fecha: today(), tipo: "" });
  const [fecha, setFecha] = useState(CalendarioHoy());
  const [loadingAgency, setLoadingAgency] = useState(false);
  const { textColor } = useSelector((state) => state.local);
  const location = useLocation();
  const [logoImpresion, setLogoImpresion] = useState();

  useEffect(() => {
    dispatch(updateLocation({ controlcaja: true }));
  }, []);

  // Carga el logo una sola vez al montar el componente
  useEffect(() => {
    getPrintLogo()
      .then(setLogoImpresion)
      .catch(() => {});
  }, []);

  // Al hacer click imprimir el logo ya está en estado → disponible en el DOM del render actual
  useEffect(() => {
    if (printing === true) {
      printTable(document.getElementById("cierretj-table"));
      setPrinting(false);
    }
  }, [printing]);

  async function loadData(agencia, subagencia) {
    try {
      const saaId = getSaaId();
      // Se piden todos los tipos: cada cierre (M9/M5) incluye sus premios pagados
      const result = await getCierreTJ(
        saaId,
        filters.fecha,
        agencia,
        subagencia,
      );
      return sortCierres(filterByCierre(result || [], filters.tipo));
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  function attemptLoad() {
    setLoading(true);
    loadCierreTJ().then((result) => {
      setData(result || []);
      setLoading(false);
    });
  }

  async function loadCierreTJ() {
    if (agencieSelected) {
      setData([]);
      const subAgencia =
        agencieSelected.agencyLevel === "CONCENTRATOR"
          ? null
          : agencieSelected.subNumber;
      return loadData(agencieSelected.number, subAgencia);
    }
    return [];
  }

  useEffect(() => {
    if (search) {
      attemptLoad();
    }
    setSearch(false);
  }, [search, agencieSelected]);

  useEffect(() => {
    setData([]);
    const path = location.pathname.split("/");
    if (hasAgencyInPath(path)) {
      const agency = getPresentAgencyInPath(path, agencies);
      if (agency === null) {
        navigate(logOutPath);
      } else {
        dispatch(setSelectedAgencieToConsult(agency));
        attemptLoad();
      }
    }
  }, []);

  // Columnas del requerimiento funcional, más agencia y subagencia: el número de
  // terminal se repite entre subagencias y sin ellas las filas no se distinguen
  const VISIBLE_COLUMNS = [
    "c_agent",
    "c_subag",
    "c_maqui",
    "d_juego",
    "f_cietj",
    "validos",
    "cantidad",
    "i_apost",
    "cierretj",
  ];

  // Se etiqueta por tipo numérico: el texto de cierretj no distingue válidos de premios
  const getCierreTipoLabel = (row) => {
    const labelKey = getCierreLabelKey(row.tipo);
    return labelKey ? t(labelKey) : (row.cierretj ?? "-");
  };

  const getColumns = () => (data?.length ? VISIBLE_COLUMNS : []);

  const isNumeric = (val) => !isNaN(parseFloat(val)) && isFinite(val);

  const exportToExcel = () => {
    Swal.fire({
      title: t("excelConfirm"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "green",
      cancelButtonColor: "#d33",
      cancelButtonText: t("cancel"),
      confirmButtonText: t("accept"),
    }).then((result) => {
      if (result.isConfirmed) {
        // Mismas columnas y encabezados que la grilla
        const rows = data.map((row) =>
          Object.fromEntries(
            VISIBLE_COLUMNS.map((col) => [
              getColumnLabel(col),
              col === "cierretj" ? getCierreTipoLabel(row) : row[col],
            ]),
          ),
        );
        exportExcel(rows, t("controlCierresTJ"));
      }
    });
  };

  const handleConsulting = (agencie) => {
    if (agencie) {
      agencieSearchByNumber(activeOrganization?.id, { number: agencie?.number })
        .then((response) => {
          dispatch(setConcentrator(response));
          dispatch(setSelectedAgencieToConsult(agencie));
          setLoadingAgency(false);
        })
        .then(() => {
          setSearch(true);
        })
        .catch((e) => {
          console.log(e);
          setLoadingAgency(false);
        });
    }
  };

  const COLUMN_LABELS = {
    c_agent: "Agencia",
    c_subag: "Subagencia",
    c_maqui: "Terminal",
    d_juego: "Juego",
    f_cietj: "Fecha",
    validos: "Concepto",
    cantidad: "Cantidad",
    i_apost: "Importe ($)",
    cierretj: "Cierre",
  };

  // Columnas que son números enteros (IDs), no importes monetarios
  const INTEGER_COLUMNS = new Set([
    "c_agent",
    "c_subag",
    "c_maqui",
    "cantidad",
  ]);

  const getColumnLabel = (col) => COLUMN_LABELS[col] ?? col;

  const headerCellSx = {
    backgroundColor: "primary.main",
    color: textColor,
    fontSize: "0.8em",
    fontWeight: 500,
    whiteSpace: "nowrap",
    padding: "6px 8px",
  };

  const columns = getColumns();

  return (
    <Grid container className={s.container}>
      <Grid item container xs={11} className={s.header}>
        <Grid item lg={6} md={6} xs={12} className={s.titleContainer}>
          <ReceiptLongIcon sx={{ fontSize: "1.8rem", color: "primary.main" }} />
          <h4>{t("controlCierresTJ")}</h4>
        </Grid>

        <Grid item xs={12} md={6} lg={6} className={s.filters}>
          <Button
            disabled={!agencieSelected}
            variant="contained"
            className={s.filterButton}
            onClick={() => setOpenModal(true)}
          >
            <TuneRoundedIcon
              sx={{ color: "secondary.light", fontSize: "1.8rem" }}
            />
            <p style={{ fontSize: "0.9rem" }}>{t("filter")}</p>
          </Button>
          {searching && (
            <Button
              variant="contained"
              sx={{ ml: 1 }}
              className={s.filterButton}
              onClick={() => {
                setFilters({ fecha: today(), tipo: "" });
                setFecha(CalendarioHoy());
                setData([]);
                setSearching(false);
                setSearch(false);
              }}
            >
              <p style={{ fontSize: "0.9rem" }}>{t("cleanFilters")}</p>
            </Button>
          )}
          {loadingAgency ? (
            <Box sx={{ mx: 7 }}>
              <CircularProgress size="2rem" />
            </Box>
          ) : (
            <Button
              sx={{ ml: 1 }}
              onClick={() => setShowModal(true)}
              color="primary"
              className={s.filterButton}
              variant="contained"
            >
              <p style={{ fontSize: "0.7rem" }}>{t("selectAgency")}</p>
            </Button>
          )}
        </Grid>
      </Grid>

      <Grid className={s.selectAgency} item xs={11}>
        <p style={{ fontSize: "1.3rem", fontWeight: "300" }}>
          {agencieSelected?.name
            ? `${t("salePoint")}: (${agencieSelected.number}${
                agencieSelected.subNumber !== null
                  ? " - " + agencieSelected.subNumber
                  : ""
              }) ${agencieSelected.name}`
            : t("agencyNotSelected")}
        </p>
        {showModal && (
          <AgencySelectModal
            open={showModal}
            setOpen={setShowModal}
            onSelect={handleConsulting}
          />
        )}
      </Grid>

      <Grid
        item
        container
        xs={11}
        sx={{ mt: 3, justifyContent: "flex-end", mb: 3 }}
      >
        <Button
          disabled={!data?.length}
          size="small"
          variant="contained"
          sx={{ mr: 1, height: "2.5rem" }}
          className={s.optionButton}
          onClick={() => setPrinting(true)}
        >
          <PrintOutlinedIcon
            sx={{ fontSize: "1.5rem", color: textColor }}
            className={s.iconOptions}
          />
        </Button>
        <Button
          disabled={!data?.length}
          size="small"
          variant="contained"
          sx={{ height: "2.5rem" }}
          className={s.optionButton}
          onClick={exportToExcel}
        >
          <NewspaperOutlinedIcon
            sx={{ fontSize: "1.5rem", color: textColor }}
            className={s.iconOptions}
          />
        </Button>
      </Grid>

      {loading ? (
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress size="3rem" />
        </Grid>
      ) : (
        <>
          {data?.length ? (
            <Grid
              item
              container
              xs={11}
              className={s.infoContainer}
              id="cierretj-table"
            >
              {printing && (
                <div className="print-header" style={{ display: "none" }}>
                  <div className="print-header-logo">
                    {logoImpresion?.imgBase64?.length ? (
                      <img src={logoImpresion.imgBase64} alt="logo" />
                    ) : null}
                  </div>
                  <h4 className="print-header-title texto">
                    {t("controlCierresTJ")}
                  </h4>
                  <div className="print-header-date texto">
                    <div>
                      {t("date")}: {filters.fecha}
                    </div>
                    <div>{new Date().toLocaleString("es-AR")}</div>
                  </div>
                </div>
              )}

              <TableContainer className={s.tableContainer}>
                <Table
                  className="print-table"
                  stickyHeader
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      padding: "6px 8px",
                      fontSize: "0.82em",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell key={col} sx={headerCellSx} align="center">
                          {getColumnLabel(col)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row, i) => (
                      <TableRow key={i}>
                        {columns.map((col) => (
                          <TableCell
                            key={col}
                            align={
                              isNumeric(row[col]) && !INTEGER_COLUMNS.has(col)
                                ? "right"
                                : "center"
                            }
                          >
                            {col === "cierretj" ? (
                              getCierreTipoLabel(row)
                            ) : isNumeric(row[col]) ? (
                              INTEGER_COLUMNS.has(col) ? (
                                row[col]
                              ) : (
                                <Currency value={row[col]} />
                              )
                            ) : (
                              (row[col] ?? "-")
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ) : (
            <Grid
              item
              xs={11}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <p style={{ fontSize: "1.8rem", fontWeight: 300 }}>
                {searching ? t("noResults") : t("agencyNotSelected")}
              </p>
            </Grid>
          )}
        </>
      )}

      <ModalFiltros
        open={openModal}
        setOpen={setOpenModal}
        setFilters={setFilters}
        setSearching={setSearching}
        setSearch={setSearch}
        fecha={fecha}
        setFecha={setFecha}
      />
    </Grid>
  );
};
